import {
  afterNextRender,
  afterRenderEffect,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { ConversationEntry } from 'app/shared/agent/agent-conversation.store';
import {
  AgentConfirmationCard,
  ConfirmationDetail,
} from 'insight/src/app/shared/agent/ui/agent-confirmation-card/agent-confirmation-card';
import { AgentInputRequest } from 'insight/src/app/shared/agent/ui/agent-input-request/agent-input-request';
import { AgentMessageBubble } from 'insight/src/app/shared/agent/ui/agent-message-bubble/agent-message-bubble';
import { ConfirmationRequiredPayload, InputRequestedPayload } from 'app/shared/models/agent.model';
import { FormaScrollShadow, FormaScrollShadowAppearance, FormaScrollShadowEdge } from 'forma';

/**
 * Scrollable conversation log. Renders the entry list and a typing indicator
 * while the agent is working, auto-scrolling to the latest content.
 *
 * When typewriter is true, live-streamed assistant messages are revealed
 * character-by-character at a controlled rate. Scrolling follows rendered
 * content and layout changes, including expanding inline action widgets.
 */
@Component({
  selector: 'app-agent-thread',
  imports: [AgentMessageBubble, AgentConfirmationCard, AgentInputRequest, FormaScrollShadow],
  host: { class: 'agent-thread' },
  template: `
    <div
      #scroll
      class="scroll"
      role="log"
      aria-live="polite"
      aria-relevant="additions text"
      (scroll)="onScroll()"
      (wheel)="markUserScroll()"
      (touchmove)="markUserScroll()"
      (keydown)="markUserScroll()"
      (pointerdown)="markUserScroll(true)"
      (pointerup)="markUserScroll()"
      [formaScrollShadow]="scrollShadow()"
      [scrollShadowAppearance]="scrollShadowAppearance()">
      <div #content class="content">
        @for (message of visible(); track message.id) {
          <app-agent-message [entry]="message" />
        }
        @if (showTyping()) {
          <div class="typing" role="status" aria-label="Assistant is typing">
            <span></span>
            <span></span>
            <span></span>
          </div>
        }
        @if (pendingInput(); as input) {
          <app-agent-input-request
            class="thread-input"
            [payload]="input"
            [validationError]="inputValidationError()"
            [otherLabel]="inputOtherLabel()"
            (submitted)="inputSubmitted.emit($event)"
            (skipped)="inputSkipped.emit()"></app-agent-input-request>
        }
        @if (pendingConfirmation(); as confirmation) {
          <app-agent-confirmation
            [confirmation]="confirmation"
            [approveLabel]="confirmationApproveLabel()"
            [rejectLabel]="confirmationRejectLabel()"
            [feedbackLabel]="confirmationFeedbackLabel()"
            [saveLabel]="confirmationSaveLabel()"
            [cancelLabel]="confirmationCancelLabel()"
            [details]="confirmationDetails()"
            (approve)="confirmationApproved.emit()"
            (reject)="confirmationRejected.emit($event)"></app-agent-confirmation>
        }
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        min-height: 0;
      }

      .scroll {
        height: 100%;
        overflow-y: auto;
        scroll-behavior: smooth;
        padding-right: 8px;
      }

      .content {
        display: flex;
        flex-direction: column;
        gap: 24px;
      }

      .scroll.history-enter {
        animation: agent-history-enter 300ms ease-out;
      }

      @keyframes agent-history-enter {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }

      .typing {
        display: inline-flex;
        gap: 4px;
        align-items: center;
        padding: 4px 0;
      }

      .typing span {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background-color: var(--forma-grey-3, #828282);
        animation: agent-typing 1.2s infinite ease-in-out;
      }

      .typing span:nth-child(2) {
        animation-delay: 0.2s;
      }

      .typing span:nth-child(3) {
        animation-delay: 0.4s;
      }

      @keyframes agent-typing {
        0%,
        60%,
        100% {
          opacity: 0.3;
        }
        30% {
          opacity: 1;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .scroll {
          scroll-behavior: auto;
        }

        .scroll.history-enter,
        .typing span {
          animation: none;
        }
      }
    `,
  ],
})
export class AgentChatThread {
  readonly messages = input.required<ConversationEntry[]>();
  readonly busy = input<boolean>(false);
  /** Enable typewriter drain for live-streamed assistant messages. */
  readonly typewriter = input<boolean>(false);
  /** Shadows at the scrolled edges of the thread; off unless a surface asks for them. */
  readonly scrollShadow = input<FormaScrollShadowEdge | null>(null);
  readonly scrollShadowAppearance = input<FormaScrollShadowAppearance>('shadow');
  /** When set, renders the action-confirmation widget inline at the bottom of the flow. */
  readonly pendingConfirmation = input<ConfirmationRequiredPayload | null>(null);
  readonly confirmationApproveLabel = input<string>('Approve');
  readonly confirmationRejectLabel = input<string>('Reject');
  readonly confirmationCancelLabel = input<string>('Cancel');
  readonly confirmationFeedbackLabel = input<string>('What would you like to change?');
  readonly confirmationSaveLabel = input<string>('Save changes');
  readonly confirmationDetails = input<ConfirmationDetail[]>([]);
  readonly confirmationApproved = output<void>();
  readonly confirmationRejected = output<string>();
  /** When set, renders the answer widget inline at the bottom of the flow. */
  readonly pendingInput = input<InputRequestedPayload | null>(null);
  readonly inputValidationError = input<string | null>(null);
  readonly inputOtherLabel = input<string>('Other…');
  readonly inputSubmitted = output<string>();
  readonly inputSkipped = output<void>();

  /** The entries actually rendered in the template. */
  protected readonly visible = signal<ConversationEntry[]>([]);

  /**
   * When typewriter is on, hide the typing indicator once text starts
   * appearing — only show it while waiting for the first character.
   */
  protected readonly showTyping = computed(() => {
    if (!this.busy()) return false;
    if (!this.typewriter()) return true;
    // In typewriter mode: show dots only until text starts being revealed.
    const vis = this.visible();
    for (let i = vis.length - 1; i >= 0; i--) {
      if (vis[i].tokens !== undefined) {
        return vis[i].text.length === 0;
      }
    }
    return true;
  });

  private readonly scrollContainer = viewChild('scroll', { read: ElementRef<HTMLElement> });
  private readonly scrollContent = viewChild<ElementRef<HTMLElement>>('content');
  private readonly destroyRef = inject(DestroyRef);

  // Typewriter drain state.
  // Chars queued for display but not yet revealed.
  private drainQueue = '';
  // ID of the entry currently being drained.
  private drainEntryId: number | null = null;
  // How many chars of the streaming entry have been queued so far.
  // Stored as a plain number so we never read visible() inside the effect,
  // which would cause infinite re-runs every time the drain updates visible.
  private totalQueued = 0;
  private rafId: number | null = null;
  private lastTick = 0;
  private pendingTypewriterScroll = false;
  private hasPresentedMessages = false;
  /**
   * Whether the user is at the newest message. The user scrolling away unpins it, so a finished
   * reply, a resize or a new card doesn't pull them away from what they are reading; reaching the
   * bottom again, or sending a message, pins it again.
   */
  private pinnedToBottom = true;
  /**
   * Until when a scroll counts as the user's (wheel, touch, keys, dragging the scrollbar). Our own
   * scrolls, smooth ones included, never unpin; only the user's input does.
   */
  private userScrollUntil = 0;
  private static readonly USER_SCROLL_WINDOW_MS = 1000;
  /** While the pointer is down, e.g. dragging the scrollbar. */
  private static readonly POINTER_SCROLL_WINDOW_MS = 30000;
  /** The highest entry id rendered so far. */
  private lastEntryId: number | null = null;
  private static readonly PIN_THRESHOLD_PX = 48;
  private static readonly CHARS_PER_SECOND = 100;

  constructor() {
    this.destroyRef.onDestroy(() => {
      if (this.rafId !== null) cancelAnimationFrame(this.rafId);
    });

    effect(() => {
      const list = this.messages();
      const tw = this.typewriter();

      if (!tw) {
        this.visible.set(list);
        return;
      }

      // Find the current streaming entry (tokens !== undefined means live).
      let streamingIdx = -1;
      for (let i = list.length - 1; i >= 0; i--) {
        if (list[i].tokens !== undefined) {
          streamingIdx = i;
          break;
        }
      }

      if (streamingIdx === -1) {
        const drainingIdx = list.findIndex((entry) => entry.id === this.drainEntryId);
        const draining = list[drainingIdx];
        if (draining && (this.drainQueue.length > 0 || draining.text.length > this.totalQueued)) {
          const revealed = this.totalQueued - this.drainQueue.length;
          this.drainQueue = draining.text.slice(revealed);
          this.totalQueued = draining.text.length;
          this.visible.set([
            ...list.slice(0, drainingIdx),
            { ...draining, text: draining.text.slice(0, revealed) },
            ...list.slice(drainingIdx + 1),
          ]);
          this.startDrain(drainingIdx);
          return;
        }

        this.cancelDrain();
        this.visible.set(list);
        return;
      }

      const streaming = list[streamingIdx];

      // New streaming entry started — reset drain state.
      if (streaming.id !== this.drainEntryId) {
        this.cancelDrain();
        this.drainEntryId = streaming.id;
        // Initialise visible with empty text for the new streaming entry so
        // the bubble exists in the DOM before any characters appear.
        this.visible.set([
          ...list.slice(0, streamingIdx),
          { ...streaming, text: '' },
          ...list.slice(streamingIdx + 1),
        ]);
      }

      // Queue any chars that arrived since the last effect run.
      if (streaming.text.length > this.totalQueued) {
        this.drainQueue += streaming.text.slice(this.totalQueued);
        this.totalQueued = streaming.text.length;
        this.startDrain(streamingIdx);
      }
    });

    afterRenderEffect(() => {
      const entries = this.visible();
      // The user's own new message always comes into view (ids only grow).
      const seen = this.lastEntryId ?? -1;
      if (entries.some((entry) => entry.id > seen && entry.kind === 'user'))
        this.pinnedToBottom = true;
      this.lastEntryId = Math.max(seen, ...entries.map((entry) => entry.id));
      this.showTyping();
      this.pendingInput();
      this.pendingConfirmation();
      this.inputValidationError();
      this.scrollToBottom(this.pendingTypewriterScroll ? 'instant' : 'auto');
      this.pendingTypewriterScroll = false;
    });

    afterNextRender(() => {
      const element = this.scrollContainer()?.nativeElement;
      const content = this.scrollContent()?.nativeElement;
      if (!element || !content) return;
      const observer = new ResizeObserver(() => this.scrollToBottom());
      observer.observe(element);
      observer.observe(content);
      this.destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  private startDrain(streamingIdx: number): void {
    if (this.rafId !== null) return; // already running

    const drain = (now: number): void => {
      const elapsed = this.lastTick ? now - this.lastTick : 16;
      this.lastTick = now;

      const take = Math.max(1, Math.round((AgentChatThread.CHARS_PER_SECOND * elapsed) / 1000));
      const chars = Math.min(this.drainQueue.length, take);

      if (chars > 0) {
        this.pendingTypewriterScroll = true;
        const chunk = this.drainQueue.slice(0, chars);
        this.drainQueue = this.drainQueue.slice(chars);
        this.visible.update((arr) => {
          const entry = arr[streamingIdx];
          if (!entry) return arr;
          const copy = [...arr];
          copy[streamingIdx] = { ...entry, text: entry.text + chunk };
          return copy;
        });
      }

      if (this.drainQueue.length > 0) {
        this.rafId = requestAnimationFrame(drain);
      } else {
        this.rafId = null;
        this.lastTick = 0;
      }
    };

    this.rafId = requestAnimationFrame(drain);
  }

  private cancelDrain(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.drainQueue = '';
    this.drainEntryId = null;
    this.totalQueued = 0;
    this.lastTick = 0;
  }

  protected markUserScroll(pointerDown = false): void {
    this.userScrollUntil =
      performance.now() +
      (pointerDown
        ? AgentChatThread.POINTER_SCROLL_WINDOW_MS
        : AgentChatThread.USER_SCROLL_WINDOW_MS);
  }

  protected onScroll(): void {
    const element = this.scrollContainer()?.nativeElement;
    if (!element) return;
    const distance = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (distance <= AgentChatThread.PIN_THRESHOLD_PX) this.pinnedToBottom = true;
    else if (performance.now() < this.userScrollUntil) this.pinnedToBottom = false;
  }

  /** A reply is streaming in or still being revealed: it is followed even when scrolled away. */
  private isAnswering(): boolean {
    return this.rafId !== null || this.visible().some((entry) => entry.tokens !== undefined);
  }

  private scrollToBottom(behavior: ScrollBehavior = 'auto'): void {
    const element = this.scrollContainer()?.nativeElement;
    if (!element) return;

    const messages = this.visible();
    if (messages.length === 0) {
      this.hasPresentedMessages = false;
      this.pinnedToBottom = true;
      element.classList.remove('history-enter');
    } else if (this.hasPresentedMessages && !this.pinnedToBottom && !this.isAnswering()) {
      return;
    } else if (!this.hasPresentedMessages && element.clientHeight > 0) {
      this.hasPresentedMessages = true;
      if (messages.some((entry) => entry.tokens === undefined)) {
        const bottom = Math.max(0, element.scrollHeight - element.clientHeight);
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        element.scrollTo({
          top: reducedMotion ? bottom : Math.max(0, bottom - element.clientHeight / 2),
          behavior: 'instant',
        });
        if (!reducedMotion) element.classList.add('history-enter');
      }
    }

    element.scrollTo({ top: element.scrollHeight, behavior });
  }
}
